import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Card, Form } from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { MODULES, ROUTES } from '@common/constants';
import { fileUpload, getSignedUrl } from '@common/utils';
import CommonHeader from '@components/CommonHeader';
import { AppContext } from '@context';

import CommonTabHeader from '../../../components/CommonTabHeader';
import history from '../../../historyData';
import OrganizationMastersTab from '../../organizations/components/OrganizationMastersTab';
import RoofExtensionsForm from '../components/RoofExtensionsForm';
import {
  CREATE_ROOF_EXTENSION,
  UPDATE_ROOF_EXTENSION,
} from '../graphql/Mutations';
import { GET_ROOF_EXTENSIONS, GET_SIGNED_URL } from '../graphql/Queries';

const CreateRoofExtensions = ({ isOrganization = false }) => {
  const { mode, id, orgId, orgMode } = useParams();
  const [form] = Form?.useForm();
  const { dispatch } = useContext(AppContext);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isView, setIsView] = useState(false);
  const [roofExtensionLogo, setRoofExtensionLogo] = useState([]);

  const [createRoofExtension, { loading: createLoading }] = useMutation(
    CREATE_ROOF_EXTENSION,
  );

  const [updateRoofExtension, { loading: updateLoading }] = useMutation(
    UPDATE_ROOF_EXTENSION,
  );

  const [getRoofExtension, { data: roofExtensionData }] = useLazyQuery(
    GET_ROOF_EXTENSIONS,
  );

  const fetchRoofExtension = async () => {
    try {
      await getRoofExtension({
        variables: { where: { id } },
        fetchPolicy: 'network-only',
      });
    } catch (error) {
      return error;
    }
  };

  useEffect(() => {
    if (mode === 'view') {
      setIsView(true);
    }
    if (id) {
      fetchRoofExtension();
    }
  }, []);

  const baseRoute = `${
    isOrganization ? `${ROUTES?.ORGANIZATIONS}/${orgMode}/${orgId}` : ''
  }${ROUTES?.MASTER}${ROUTES?.ROOF_EXTENSIONS}`;

  const onEdit = () => {
    setIsView(false);
    history?.push(`${baseRoute}/edit/${id}`);
  };

  const onCancelEdit = () => {
    setIsView(true);
    history?.push(baseRoute);
  };

  const onFinish = async (values) => {
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: false,
    });

    const { isActive, tooltipTitle, tooltipBody, ...rest } = values;

    const dataToSend = {
      ...rest,
      isActive: JSON?.parse(isActive),
      tooltip: { title: tooltipTitle, body: tooltipBody },
    };

    try {
      let imgKey;
      if (values?.imageUrl && !roofExtensionLogo?.[0]?.url) {
        await getSignedUrl(
          values?.imageUrl?.[0]?.originFileObj,
          GET_SIGNED_URL,
        )?.then(async (res) => {
          const signedImageUrl =
            res?.getRoofExtensionUploadSignedUrl?.signedUrl;
          imgKey = res?.getRoofExtensionUploadSignedUrl?.key;
          await fileUpload(
            signedImageUrl,
            values?.imageUrl?.[0]?.originFileObj,
          );
          dataToSend.imageUrl = imgKey;
        });
      } else if (roofExtensionLogo?.[0]?.url) {
        delete dataToSend.imageUrl;
      }
      if (id) {
        try {
          await updateRoofExtension({
            variables: {
              where: { id },
              data: {
                ...dataToSend,
              },
            },
            onCompleted: () => onCancelEdit(),
          });
        } catch (error) {
          return error;
        }
      } else {
        try {
          await createRoofExtension({
            variables: {
              data: {
                ...dataToSend,
              },
            },
            onCompleted: () => onCancelEdit(),
          });
        } catch (error) {
          return error;
        }
      }
    } catch (error) {
      return error;
    }
  };

  useEffect(() => {
    if (roofExtensionData?.roofExtension?.data) {
      const dataToPass = {
        key: roofExtensionData?.roofExtension?.data?.key,
        label: roofExtensionData?.roofExtension?.data?.label,
        description: roofExtensionData?.roofExtension?.data?.description,
        imageUrl: roofExtensionData?.roofExtension?.data?.imageUrl,
        isActive: JSON.stringify(
          roofExtensionData?.roofExtension?.data?.isActive,
        ),
        tooltipTitle: roofExtensionData?.roofExtension?.data?.tooltip?.title,
        tooltipBody: roofExtensionData?.roofExtension?.data?.tooltip?.body,
      };
      setRoofExtensionLogo([
        { url: roofExtensionData?.roofExtension?.data?.imageUrl },
      ]);
      form?.setFieldsValue(dataToPass);
    }
  }, [roofExtensionData]);

  const handleShowPrompt = () => {
    setIsDisabled(false);
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: true,
    });
  };

  const renderHeaderRightContent = () => (
    <>
      {isView ? (
        <Button className="common-button" size="middle" onClick={onEdit}>
          Edit
        </Button>
      ) : (
        <>
          <Button
            type="primary"
            className="common-button mr-16"
            size="middle"
            onClick={onCancelEdit}
          >
            Cancel
          </Button>
          <Button
            className="common-button"
            size="middle"
            onClick={form?.submit}
            loading={createLoading || updateLoading}
            disabled={createLoading || updateLoading || isDisabled}
          >
            Save
          </Button>
        </>
      )}
    </>
  );

  const roofExtensionHeading = id ? `${mode} House Type` : 'Add House Type';

  const renderHeader = () => (
    <CommonTabHeader heading={roofExtensionHeading}>
      {renderHeaderRightContent()}
    </CommonTabHeader>
  );

  const roofExtensionsCard = (
    <Card
      className={
        isOrganization
          ? 'full-height-without-nav-vertical-tabs'
          : 'full-height-without-nav'
      }
    >
      <RoofExtensionsForm
        form={form}
        onFinish={onFinish}
        handleShowPrompt={handleShowPrompt}
        isView={isView}
        roofExtensionLogo={roofExtensionLogo}
        setRoofExtensionLogo={setRoofExtensionLogo}
      />
    </Card>
  );

  return (
    <>
      <CommonHeader
        heading={isOrganization ? MODULES?.MASTER : roofExtensionHeading}
        customLink={ROUTES?.MASTER}
      >
        {!isOrganization && <>{renderHeaderRightContent()}</>}
      </CommonHeader>
      {isOrganization ? (
        <OrganizationMastersTab>
          {renderHeader()}
          {roofExtensionsCard}
        </OrganizationMastersTab>
      ) : (
        roofExtensionsCard
      )}
    </>
  );
};

export default CreateRoofExtensions;
